import { useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { listEventGuests } from '../api/owner'
import type { EventGuest, GuestSort } from '../api/ownerTypes'
import { GuestAvatar } from '../components/Monogram'
import { Spinner } from '../components/icons'
import { formatTime, plural } from '../lib/format'
import { useEvent } from './eventContext'
import { EventScreen } from './components/EventScreen'

const pageSize = 20

const sorts: { id: GuestSort; label: string }[] = [
  { id: 'Uploads', label: 'Meeste foto’s' },
  { id: 'Recent', label: 'Recentste' },
  { id: 'Name', label: 'A–Z' },
]

/**
 * Ronde 20N en 20P — de gastenlijst.
 *
 * Op mobiel kaarten onder elkaar, op desktop een tabel met kolommen voor
 * foto's, video's en het laatste uploadmoment. "Bekijk" opent de galerij van
 * het gastenportaal, gefilterd op die gast (ronde 20O) — dat scherm bestond al.
 */
export function EventGuestsPage() {
  const { subject } = useEvent()
  const [sort, setSort] = useState<GuestSort>('Uploads')

  const guests = useInfiniteQuery({
    queryKey: ['owner', 'guests', subject.id, sort],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => listEventGuests(subject.id, sort, pageParam, pageSize),
    getNextPageParam: (page, all) => {
      const loaded = all.reduce((sum, item) => sum + item.guests.length, 0)

      return loaded < page.totalCount ? loaded : undefined
    },
  })

  const rows = guests.data?.pages.flatMap((page) => page.guests) ?? []
  const total = guests.data?.pages.at(0)?.totalCount ?? 0
  const totalMedia = guests.data?.pages.at(0)?.totalMediaCount ?? 0
  const remaining = total - rows.length

  const tabs = (
    <div className="flex gap-2">
      {sorts.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={option.id === sort}
          onClick={() => setSort(option.id)}
          className={
            'shrink-0 rounded-full px-4 py-2.25 text-sm whitespace-nowrap transition-colors lg:px-4.5 lg:py-2.5 ' +
            (option.id === sort
              ? 'bg-ink text-shell font-medium'
              : 'border-line text-ink-muted hover:border-line-warm hover:bg-tint/50 hover:text-ink border')
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  )

  return (
    <EventScreen
      title="Gasten"
      eventTitle={`${subject.guestCount} geregistreerd · ${plural(subject.mediaCount, 'foto', 'foto’s')}`}
      desktopSubtitle={`${total} geregistreerd · samen ${plural(totalMedia, 'foto', 'foto’s')}`}
      aside={tabs}
    >
      {/* Op mobiel staan de sorteerpillen boven de lijst; op desktop naast de
          titel, en daar zijn ze hierboven al meegegeven. */}
      <div className="scroll-x-clean -mx-5.5 mb-4 flex px-5.5 lg:hidden">{tabs}</div>

      {guests.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner className="text-accent size-6" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-ink-soft py-12 text-center text-sm">
          Nog geen gasten. Zodra iemand de QR scant en zijn naam invult, staat hij hier.
        </p>
      ) : (
        <>
          {/* --- Mobiel: kaarten ---------------------------------- */}
          <div className="flex flex-col gap-2 lg:hidden">
            {rows.map((guest) => (
              <GuestCard key={guest.id} guest={guest} guestUrl={subject.guestUrl} />
            ))}
          </div>

          {/* --- Desktop: tabel ----------------------------------- */}
          <div className="rounded-card border-line-card bg-surface hidden overflow-hidden border lg:block">
            <div className="text-ink-faint bg-band flex items-center gap-5 px-5.5 py-3.5 text-xs tracking-[0.14em] uppercase">
              <div className="flex-1">Gast</div>
              <div className="w-27.5 text-right">Foto’s</div>
              <div className="w-27.5 text-right">Video’s</div>
              <div className="w-37.5 text-right">Laatste upload</div>
              <div className="w-47.5" />
            </div>

            {rows.map((guest) => (
              <div
                key={guest.id}
                className="border-line-menu flex items-center gap-5 border-t px-5.5 py-3.75"
              >
                <div className="flex flex-1 items-center gap-3.25">
                  <GuestAvatar displayName={guest.displayName} size={38} />
                  <div>
                    <p className="text-base font-medium">{guest.displayName}</p>
                    {guest.tableNumber !== null && (
                      <p className="text-ink-faint mt-0.5 text-[13px]">Tafel {guest.tableNumber}</p>
                    )}
                  </div>
                </div>

                <div className="w-27.5 text-right text-base">{guest.photoCount}</div>
                <div className="text-ink-faint w-27.5 text-right text-base">{guest.videoCount}</div>
                <div className="text-ink-soft w-37.5 text-right text-[15px]">
                  {guest.lastUploadUtc ? formatTime(guest.lastUploadUtc) : '—'}
                </div>
                <div className="flex w-47.5 justify-end">
                  <GalleryLink
                    guestUrl={subject.guestUrl}
                    guestId={guest.id}
                    label="Bekijk in galerij"
                  />
                </div>
              </div>
            ))}

            {remaining > 0 && (
              <button
                type="button"
                onClick={() => void guests.fetchNextPage()}
                className="border-line-card text-accent bg-band flex w-full items-center justify-center border-t px-5.5 py-3.75 text-[15px]"
              >
                {guests.isFetchingNextPage
                  ? 'Laden…'
                  : `Nog ${plural(remaining, 'gast', 'gasten')} laden`}
              </button>
            )}
          </div>

          {remaining > 0 && (
            <button
              type="button"
              onClick={() => void guests.fetchNextPage()}
              className="text-accent mt-4 w-full py-3 text-center text-[15px] lg:hidden"
            >
              {guests.isFetchingNextPage
                ? 'Laden…'
                : `Nog ${plural(remaining, 'gast', 'gasten')} laden`}
            </button>
          )}
        </>
      )}
    </EventScreen>
  )
}

function GuestCard({ guest, guestUrl }: { guest: EventGuest; guestUrl: string }) {
  const total = guest.photoCount + guest.videoCount

  return (
    <div className="rounded-notice border-line-card bg-surface border p-3.25 px-3.5">
      <div className="flex items-center gap-3">
        <GuestAvatar displayName={guest.displayName} size={40} />

        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold">{guest.displayName}</p>
          <p className="text-ink-faint mt-0.75 text-[13px]">
            {plural(total, 'foto', 'foto’s')}
            {guest.lastUploadUtc && ` · laatste ${formatTime(guest.lastUploadUtc)}`}
          </p>
        </div>

        <GalleryLink guestUrl={guestUrl} guestId={guest.id} label="Bekijk" />
      </div>
    </div>
  )
}

/**
 * Opent het gastenportaal met de galerij gefilterd op deze gast — precies het
 * scherm uit ronde 20O. De link bevat het invite token, want het portaal wil
 * dat hebben.
 */
function GalleryLink({
  guestUrl,
  guestId,
  label,
}: {
  guestUrl: string
  guestId: string
  label: string
}) {
  const [base, token] = guestUrl.split('#')

  return (
    <a
      href={`${base}/album?tab=mijn&gast=${guestId}${token ? `#${token}` : ''}`}
      target="_blank"
      rel="noreferrer"
      className="border-line text-ink-muted hover:border-accent hover:bg-tint hover:text-accent shrink-0 rounded-full border px-3.5 py-2 text-[13px] whitespace-nowrap transition-colors lg:px-4 lg:py-2.25 lg:text-sm"
    >
      {label}
    </a>
  )
}
